import { emit, listen } from '@tauri-apps/api/event';

import { WsIceMessage } from './RtcComponent';

export interface SessionsObj {
    all: string[];
    arp: string[];
    headless: { id: string; instance: string }[];
}

interface RtcComponentOptions {
    onSessions?: (obj: SessionsObj) => void;
    onUnauthorized: () => Promise<string>;
    publicDiscovery?: boolean;
    refreshToken: () => Promise<string>;
    token: string;
}

export class TauriRtcComponent {
    ws?: WebSocket;
    onSessions: RtcComponentOptions['onSessions'];
    onUnauthorized: RtcComponentOptions['onUnauthorized'];
    refreshToken: RtcComponentOptions['refreshToken'];
    timeout?: NodeJS.Timeout;
    promiseObj?: { rej: (err: unknown) => void; res: (val: string) => void };
    #publicDiscovery: boolean;
    token: string;

    constructor({ onSessions, onUnauthorized, publicDiscovery, refreshToken, token }: RtcComponentOptions) {
        this.#publicDiscovery = publicDiscovery ?? false;
        this.refreshToken = refreshToken;
        this.token = token;
        this.connect();
        this.onSessions = onSessions;
        this.onUnauthorized = onUnauthorized;

        listen('tauri-to-window', (msg) => {
            console.log('GOT T2W MSG', msg);
            this.ws?.send(JSON.stringify(msg.payload));
        }).catch(console.error);
    }

    /** Connect to websocket and handle WebRTC signaling */
    connect() {
        this.refreshToken()
            .then((token) => {
                const queryParams = new URLSearchParams({
                    arp: '__TAURI_INTERNALS__' in window ? '1' : '0',
                    public: this.#publicDiscovery ? '1' : '0',
                    token,
                });
                const ws = new WebSocket(
                    process.env.EXPO_PUBLIC_RENKI_API_HOST
                        ? `${process.env.EXPO_PUBLIC_RENKI_API_HOST}/v1/rtc/ws/?${queryParams.toString()}`
                        : `wss://${process.env.EXPO_PUBLIC_RENKI_API_HOST!}/v1/rtc/ws/?${queryParams.toString()}`,
                );
                let shouldRetry = true;
                ws.onopen = () => {
                    console.log('WS Connected');
                    this.ws = ws;
                };
                ws.onmessage = (msg) => {
                    console.log('GOT WS MSG', msg.data);
                    const parsed = JSON.parse(msg.data as string) as WsIceMessage;

                    if (this.onSessions && parsed.type === 'sessions') {
                        this.onSessions(parsed);
                    }

                    emit('window-to-tauri', parsed).catch(console.error);
                };
                ws.onclose = (e) => {
                    if (e.code === 4000) {
                        console.log('Unauthorized, refreshing token');
                        this.onUnauthorized()
                            .then((res) => {
                                this.token = res;
                                this.connect();
                            })
                            .catch(console.error);
                    } else {
                        console.log(
                            'Socket is closed. Reconnect will be attempted in 5 seconds.',
                            'code',
                            e.code,
                            'reason',
                            e.reason,
                        );
                        ws.close();
                        setTimeout(() => {
                            if (shouldRetry) {
                                shouldRetry = false;
                                this.connect();
                            }
                        }, 5000);
                    }
                };
                ws.onerror = (err) => {
                    console.error('Socket encountered error: ', err, 'Closing socket');
                    ws.close();
                    this.ws = undefined;
                    setTimeout(() => {
                        if (shouldRetry) {
                            shouldRetry = false;
                            this.connect();
                        }
                    }, 5000);
                };
                setTimeout(() => {
                    if (!this.ws && ws.readyState === 0) {
                        console.log('WS stuck');
                        ws.close();
                        if (shouldRetry) {
                            shouldRetry = false;
                            this.connect();
                        }
                    }
                }, 5000);
            })
            .catch((error) => {
                if (error instanceof Error && error.name === 'AbortError') {
                    console.log('WS Aborted');
                    this.ws?.close();
                } else {
                    console.error(error);
                }
            });
    }

    /** Toggle public discovery query param for websocket */
    setPublicDiscovery(val: boolean) {
        this.#publicDiscovery = val;
        this.ws?.close();
    }
}
